import React, { useRef, useEffect, useLayoutEffect } from 'react'
import styles from './ConversationWindow.module.scss'
import MessageList from '../MessageList/MessageList.tsx'
import { Message } from '../../../../../utils/conversationStorage.ts'
import type { DesktopSuggestion } from '../../dashboardActionsConfig.tsx'
import type { DocumentPipelineStreamHint } from '../../../../../contexts/ConversationalAgentContext.tsx'

const PIN_PX = 100

const scrollContainerToBottom = (el: HTMLElement) => {
  el.scrollTop = el.scrollHeight
}

const isNearBottom = (el: HTMLElement) =>
  el.scrollHeight - el.scrollTop - el.clientHeight < PIN_PX

interface ConversationWindowProps {
  messages: Message[]
  onClose: () => void
  isLoading?: boolean
  matchedSuggestion?: DesktopSuggestion | null
  onSuggestionAction?: () => void
  onRequestGuide?: () => void
  allSuggestions?: DesktopSuggestion[]
  hasReachedLimit?: boolean
  remainingMessages?: number
  limit?: number
  userData?: any
  pendingImagePreview?: string | null
  isLatestMessageGuardarPost?: boolean
  showDocumentPipelineLoop?: boolean
  documentPipelinePhase?: string | null
  documentPipelineStreamHint?: DocumentPipelineStreamHint
  documentPipelinePrimaryFileOriginalName?: string | null
  documentPipelinePrimaryFileSizeBytes?: number | null
  variant?: 'dashboard' | 'floating'
  onSuggestedChipSend?: (label: string, actionId: string) => void
  onRequestOpenAttachmentPicker?: () => void
}

const ConversationWindow: React.FC<ConversationWindowProps> = ({
  messages,
  onClose,
  isLoading = false,
  matchedSuggestion,
  onSuggestionAction,
  onRequestGuide,
  allSuggestions = [],
  hasReachedLimit = false,
  remainingMessages = 0,
  limit = 0,
  userData,
  pendingImagePreview = null,
  isLatestMessageGuardarPost = false,
  showDocumentPipelineLoop = false,
  documentPipelinePhase = null,
  documentPipelineStreamHint = null,
  documentPipelinePrimaryFileOriginalName = null,
  documentPipelinePrimaryFileSizeBytes = null,
  variant = 'dashboard',
  onSuggestedChipSend,
  onRequestOpenAttachmentPicker
}) => {
  void onClose
  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const prevScrollSigRef = useRef('')

  const last = messages[messages.length - 1]
  const scrollSig = `${messages.length}:${last?.id ?? ''}:${isLoading}:${documentPipelinePhase ?? ''}:${documentPipelineStreamHint ?? ''}`

  const scrollportClass =
    variant === 'floating'
      ? `${styles.messagesScrollport} ${styles.messagesScrollportFloating}`
      : `${styles.messagesScrollport} ${styles.messagesScrollportDashboard}`

  const paddedClass =
    variant === 'floating' ? styles.messagesPaddedFloating : styles.messagesPaddedDashboard

  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    if (scrollSig !== prevScrollSigRef.current) {
      prevScrollSigRef.current = scrollSig
      scrollContainerToBottom(el)
    }
  }, [scrollSig])

  useEffect(() => {
    const container = containerRef.current
    const content = contentRef.current
    if (!container || !content) return
    let rafId = 0
    const ro = new ResizeObserver(() => {
      if (rafId !== 0) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        rafId = 0
        const c = containerRef.current
        if (!c) return
        if (isNearBottom(c)) {
          scrollContainerToBottom(c)
        }
      })
    })
    ro.observe(content)
    return () => {
      ro.disconnect()
      if (rafId !== 0) cancelAnimationFrame(rafId)
    }
  }, [])

  return (
    <div ref={containerRef} className={scrollportClass}>
      <div ref={contentRef} className={`${styles.messagesPadded} ${paddedClass}`}>
        <MessageList
          messages={messages}
          isLoading={isLoading}
          matchedSuggestion={matchedSuggestion}
          onSuggestionAction={onSuggestionAction}
          onRequestGuide={onRequestGuide}
          allSuggestions={allSuggestions}
          hasReachedLimit={hasReachedLimit}
          remainingMessages={remainingMessages}
          limit={limit}
          userData={userData}
          pendingImagePreview={pendingImagePreview}
          isLatestMessageGuardarPost={isLatestMessageGuardarPost}
          showDocumentPipelineLoop={showDocumentPipelineLoop}
          documentPipelinePhase={documentPipelinePhase}
          documentPipelineStreamHint={documentPipelineStreamHint}
          documentPipelinePrimaryFileOriginalName={documentPipelinePrimaryFileOriginalName}
          documentPipelinePrimaryFileSizeBytes={documentPipelinePrimaryFileSizeBytes}
          onSuggestedChipSend={onSuggestedChipSend}
          onRequestOpenAttachmentPicker={onRequestOpenAttachmentPicker}
        />
      </div>
    </div>
  )
}

export default ConversationWindow
